/**
 * Who is working (1.1) — the roster over 2 / 4 / 13 weeks. Same roster, same
 * controls; only the span changes. Reads real shifts and blocks through the store.
 */
import { useMemo, useState } from "react";
import { Box, Button, ButtonGroup, IconButton, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { addDaysToKey, dateKeyStart, formatInBranchZone, isoWeekday, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectRosterGrid, selectRosterWeeks, type RosterCell, type RosterSpan } from "../../engine/rosterGrid";
import { territoryNames } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { useDemoState } from "../../store/useDemoStore";
import { useDemoBranchClock } from "../../components/branchClock";

const CELL_COLORS: Record<RosterCell["kind"], { bg: string; fg?: string }> = {
  // A staff member's empty day and an external's are the same empty day, so the
  // dash is the same grey in both (Pete, 2026-09-20: *"the dashes are different
  // colors between externals and internals"* — `undeclared` carried `#bbb` and
  // `off` fell through to the table's own text colour).
  off: { bg: "transparent", fg: "#bbb" },
  shift: { bg: "#d7e7f5" },
  worked: { bg: "#ececec", fg: "#555" },
  // Time off is **not working, with a word for it** (Pete, 2026-09-20: *"put
  // 'Off' in the grid cells and get rid of the cross-hatching… basically the
  // same as not working just with Off instead of a dash"*). It was a hatched
  // grey block, which gave an ordinary approved absence more weight on the grid
  // than a shift had. The word is in the table's own text colour rather than
  // the dash's grey, so a day somebody is deliberately away reads as something
  // said, not as a cell nobody filled in — and it needs no key entry.
  pto: { bg: "transparent" },
  ramp: { bg: "#e6dcf3" },
  // Overflow is a normal shift the branch reaches for **last**, so it is the
  // quietest thing on the grid that still counts as working: the same blue as a
  // shift, thinned, with the hours in ordinary text. Pete picked it from the
  // swatch page on 2026-09-20, after a saturated peach (`#f6d6bf`) read as an
  // alert, a sand (`#efe3d2`) did not suit, and a mid blue-grey (`#cdd8e2`)
  // drifted toward the grey of a worked day. Nothing sits near it now: the pale
  // blue it would once have collided with was `pattern`, and that state is gone.
  overflow: { bg: "#edf4fb" },
  undeclared: { bg: "transparent", fg: "#bbb" },
};

const DISCIPLINES = ["RN", "LPN", "PT", "PTA", "OT", "COTA", "SLP", "MSW", "HHA"];

export function RosterGrid() {
  const state = useDemoState();
  const tz = state.config.branch.timeZone;
  const [span, setSpan] = useState<RosterSpan>(2);
  const [weekStart, setWeekStart] = useState(() => weekStartKey(state.meta.demoToday));
  const [discipline, setDiscipline] = useState<string>("All");
  const [territory, setTerritory] = useState<string>("All");

  const grid = useMemo(
    () => selectRosterGrid(state, { span, weekStart, disciplines: discipline === "All" ? undefined : [discipline], territories: territory === "All" ? undefined : [territory] }),
    [state, span, weekStart, discipline, territory],
  );
  const weeks = useMemo(() => (span === 13 ? selectRosterWeeks(state, 13, weekStart) : undefined), [state, span, weekStart]);
  const rangeLabel = `${formatInBranchZone(dateKeyStart(grid.rangeStart, tz), tz, "MMM d")} – ${formatInBranchZone(dateKeyStart(grid.rangeEnd, tz), tz, "MMM d, yyyy")}`;
  const move = (dir: -1 | 1) => setWeekStart((w) => addDaysToKey(w, dir * span * 7));
  const dayHeader = (d: string) => (span === 2 ? formatInBranchZone(dateKeyStart(d, tz), tz, "EEE") : formatInBranchZone(dateKeyStart(d, tz), tz, "EEEEE"));
  const dayNumber = (d: string) => formatInBranchZone(dateKeyStart(d, tz), tz, "d");
  const compact = span === 4;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={() => move(-1)} aria-label="Previous">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          {rangeLabel}
        </Typography>
        <IconButton size="small" onClick={() => move(1)} aria-label="Next">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">Span</Typography>
          <ButtonGroup size="small">
            {[2, 4, 13].map((n) => (
              <Button key={n} variant={span === n ? "contained" : "outlined"} onClick={() => setSpan(n as RosterSpan)}>
                {n} wk
              </Button>
            ))}
          </ButtonGroup>
        </Stack>
        <Button size="small" variant="outlined" onClick={() => setWeekStart(weekStartKey(state.meta.demoToday))}>
          Today
        </Button>
        <Select size="small" value={discipline} onChange={(e) => setDiscipline(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="All">Discipline: All</MenuItem>
          {DISCIPLINES.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </Select>
        <Select size="small" value={territory} onChange={(e) => setTerritory(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="All">Territory: All</MenuItem>
          {territoryNames(state.config.branch).map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {weeks ? (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Week of</TableCell>
                {weeks.weeks.map((w) => (
                  <TableCell key={w} align="center" sx={{ fontWeight: 600, bgcolor: w === weekStartKey(state.meta.demoToday) ? "#fff4d6" : undefined }}>
                    {formatInBranchZone(dateKeyStart(w, tz), tz, "MMM d")}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={weeks.weeks.length + 1} sx={{ bgcolor: "#f7f7f7", fontWeight: 600, fontSize: 12 }}>
                  Scheduled clinician-days
                </TableCell>
              </TableRow>
              {weeks.rows.map((r) => (
                <TableRow key={r.label} sx={r.label.startsWith("SOC") ? { "& td": { fontWeight: 600 } } : undefined}>
                  <TableCell>{r.label}</TableCell>
                  {r.values.map((v) => (
                    <TableCell key={v.weekStart} align="center">
                      {v.count}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: compact ? 1100 : 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 230, fontSize: 11, color: "text.secondary" }}>Week of</TableCell>
                {grid.weeks.map((w) => (
                  <TableCell key={w} colSpan={7} align="center" sx={{ fontSize: 11, color: "text.secondary" }}>
                    {formatInBranchZone(dateKeyStart(w, tz), tz, "MMM d")} — {formatInBranchZone(dateKeyStart(addDaysToKey(w, 6), tz), tz, "MMM d")}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Clinician</TableCell>
                {/* Today marks the **date cell only** (Pete, 2026-09-20: *"only highlight the
                    date cell at the top instead of the whole column since it blows away the
                    other colors"*) — running it down the column overpainted every cell's
                    own colour, which is the one thing the grid is for. */}
                {grid.days.map((d) => (
                  <TableCell key={d} align="center" sx={{ px: 0.5, fontSize: compact ? 10 : 11.5, fontWeight: d === state.meta.demoToday ? 700 : undefined, bgcolor: d === state.meta.demoToday ? "#fff4d6" : isoWeekday(d) >= 6 ? "#fafafa" : undefined, boxShadow: d === state.meta.demoToday ? "inset 0 -3px 0 #d9a400" : undefined }}>
                    {dayHeader(d)}
                    <br />
                    {dayNumber(d)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grid.groups.map((g) => (
                <GroupRows key={g.key} label={g.label} rows={g.rows} compact={compact} />
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>SOC-capable on shift</TableCell>
                {grid.footer.map((f) => (
                  <TableCell key={f.dateKey} align="center" sx={{ fontSize: 11 }}>
                    {f.socCapableOnShift || ""}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Clinicians on shift</TableCell>
                {grid.footer.map((f) => (
                  <TableCell key={f.dateKey} align="center" sx={{ fontSize: 11 }}>
                    {f.cliniciansOnShift || ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1.5, fontSize: 11.5, color: "text.secondary" }}>
        <Legend swatch={CELL_COLORS.shift.bg} label="Working" />
        <Legend swatch={CELL_COLORS.worked.bg} label="Worked (past)" />
        <Legend swatch={CELL_COLORS.ramp.bg} label="Orientation" />
        <Legend swatch={CELL_COLORS.overflow.bg} label="Overflow" />
      </Stack>
    </Paper>
  );
}

function GroupRows({ label, rows, compact }: { label: string; rows: ReturnType<typeof selectRosterGrid>["groups"][number]["rows"]; compact: boolean }) {
  return (
    <>
      <TableRow>
        <TableCell colSpan={rows[0] ? rows[0].cells.length + 1 : 1} sx={{ bgcolor: "#f7f7f7", fontWeight: 600, fontSize: 11.5, py: 0.5 }}>
          {label}
        </TableCell>
      </TableRow>
      {rows.map((r) => (
        <TableRow key={r.handle}>
          <TableCell sx={{ py: 0.5 }}>
            <Typography variant="body2" component="span">
              {r.displayName}
            </Typography>
            {/* Only an external is worth a word beside the name now, so the
                separator goes with the subtext rather than hanging there alone. */}
            {r.meta && (
              <Typography variant="caption" color="text.secondary" component="span">
                {" · "}
                {r.meta}
              </Typography>
            )}
          </TableCell>
          {r.cells.map((c) => (
            <Cell key={c.dateKey} cell={c} compact={compact} />
          ))}
        </TableRow>
      ))}
    </>
  );
}

/**
 * One day for one clinician: **the glyph on every span**, and the hours one
 * hover away.
 *
 * The two-week grid used to print the hours on the face of each cell and the
 * four-week grid a glyph, so the same day read as `8:00–5:00` on one span and
 * `▮` on the other, and the thing that distinguishes a standing shift from an
 * overflow one — which is the whole point of the grid — was a background colour
 * on one and a letter on the other. Pete, 2026-09-22: give the two-week view the
 * same indicators and make the ranges tooltips on both.
 *
 * That reverses the *"get rid of the tooltips on the top grid"* of 2026-09-20,
 * and for the reason that was given then: the hover repeated what was already on
 * the face. It no longer is, so the hover is now the only place the hours live —
 * and it carries the hours alone. A day with no hours to give has no hover.
 */
function Cell({ cell, compact }: { cell: RosterCell; compact: boolean }) {
  const clock = useDemoBranchClock();
  const style = CELL_COLORS[cell.kind];
  const hours = cell.hours ? clock.hours(cell.hours.start, cell.hours.end) : "";
  // **The hours and nothing else** (Pete, 2026-09-22). A day whose hours are not
  // known — orientation, time off, a per diem who has not declared — has no
  // hover at all rather than one explaining its own glyph.
  const tip = hours;
  // A weekend with nobody on it is shaded the same whoever the row belongs to.
  // It used to test `kind === "off"` alone, so an external — whose empty days
  // are `undeclared`, not `off` — kept a white weekend while the staff around
  // them had a grey one (Pete, 2026-09-20).
  const emptyWeekend = cell.weekend && (cell.kind === "off" || cell.kind === "undeclared");
  const body = (
    <TableCell
      align="center"
      aria-label={tip || undefined}
      sx={{
        px: 0.25,
        py: compact ? 0.25 : 0.5,
        fontSize: compact ? 9.5 : 11,
        height: compact ? 24 : 30,
        color: style.fg,
        bgcolor: emptyWeekend ? "#fafafa" : style.bg,
        whiteSpace: "nowrap",
      }}
    >
      {cell.glyph}
    </TableCell>
  );
  return tip ? <Tooltip title={tip}>{body}</Tooltip> : body;
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box component="i" sx={{ display: "inline-block", width: 14, height: 12, border: "1px solid #d9d9d9", bgcolor: swatch }} />
      {label}
    </Box>
  );
}
